import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, Download, ExternalLink, RotateCw } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import './PDFModal.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

const PDF_OPTIONS = {
  cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  disableStream: true,
  disableRange: true,
};

const PDFModal = ({ pdfUrl, title, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    if (!containerRef.current) return;
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const onLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    setLoadError(null);
  }, []);

  const onLoadError = useCallback((err) => {
    console.error('PDFModal load error:', err);
    setLoadError(err && err.message ? err.message : String(err) || 'Error desconocido');
  }, []);

  const retry = useCallback(() => {
    setLoadError(null);
    setNumPages(null);
    setRetryKey((k) => k + 1);
  }, []);

  const pageWidth = containerWidth > 0
    ? Math.min(containerWidth - 24, 1100)
    : undefined;

  return (
    <div className="pdf-modal-overlay" onClick={onClose}>
      <div className="pdf-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-header">
          <span className="pdf-modal-title">{title}</span>
          <div className="pdf-modal-actions">
            <a href={pdfUrl} download className="pdf-modal-download">
              <Download size={16} />
              <span className="pdf-modal-download-label">Descargar</span>
            </a>
            <button className="pdf-modal-close" onClick={onClose} aria-label="Cerrar">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="pdf-modal-body" ref={containerRef}>
          {loadError ? (
            <div className="pdf-modal-error">
              <p>No se pudo cargar el PDF.</p>
              <p className="pdf-modal-error-detail">{loadError}</p>
              <div className="pdf-modal-error-actions">
                <button onClick={retry} className="pdf-modal-download">
                  <RotateCw size={16} />
                  Reintentar
                </button>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdf-modal-download pdf-modal-download--ghost"
                >
                  <ExternalLink size={16} />
                  Abrir en nueva pestaña
                </a>
                <a href={pdfUrl} download className="pdf-modal-download pdf-modal-download--ghost">
                  <Download size={16} />
                  Descargar
                </a>
              </div>
            </div>
          ) : (
            <Document
              key={retryKey}
              file={pdfUrl}
              onLoadSuccess={onLoadSuccess}
              onLoadError={onLoadError}
              loading={<div className="pdf-modal-loading">Cargando PDF…</div>}
              error={<div className="pdf-modal-loading">Error al cargar.</div>}
              options={PDF_OPTIONS}
            >
              {pageWidth && numPages > 0 && Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={`page_${i + 1}`}
                  pageNumber={i + 1}
                  width={pageWidth}
                  className="pdf-modal-page"
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  loading={<div className="pdf-modal-loading">Renderizando página…</div>}
                />
              ))}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFModal;
